/**
 * Type guard and assertion functions for doc ref eligible entity types.
 *
 * Types and runtime objects are imported from building-types/entities/.
 */
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import {
  docRefDirectEligibleEntities,
  embeddedDocRefEligibleEntities,
} from "building-types/entities/docRefDirectEligibleEntities";
import type {
  DocRefDirectEligibleEntityHandle,
  DocRefDirectEligibleEntityType,
  DocRefEligibleEntityHandle,
  DocRefEligibleEntityType,
  DocRefIndirectEligibleEntityHandle,
  DocRefIndirectEligibleEntityType,
  DocRefIneligibleEntityType,
  EmbeddedDocRefEligibleEntityType,
} from "building-types/entities/docRefEligibleEntityTypes";
import { docRefIndirectEligibleEntities } from "building-types/entities/docRefIndirectEligibleEntities";
import { docRefIneligibleEntities } from "building-types/entities/docRefIneligibleEntities";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";

// Construct arrays from runtime objects
export const docRefDirectEligibleEntityTypes = Object.keys(
  docRefDirectEligibleEntities,
) as DocRefDirectEligibleEntityType[];

export const docRefIndirectEligibleEntityTypes = Object.keys(
  docRefIndirectEligibleEntities,
) as DocRefIndirectEligibleEntityType[];

export const docRefEligibleEntityTypes = [
  ...docRefDirectEligibleEntityTypes,
  ...docRefIndirectEligibleEntityTypes,
] as const satisfies DocRefEligibleEntityType[];

export const docRefIneligibleEntityTypes = Object.keys(docRefIneligibleEntities) as DocRefIneligibleEntityType[];

export const embeddedDocRefEligibleEntityTypes = Object.keys(
  embeddedDocRefEligibleEntities,
) as EmbeddedDocRefEligibleEntityType[];

export function assertIsDocRefDirectEligibleEntityHandle(
  handle: DocEntityHandle,
): asserts handle is DocRefDirectEligibleEntityHandle {
  if (!(handle.type in docRefDirectEligibleEntities)) {
    throw new Error(`Invalid doc ref direct eligible entity handle: ${handle.type}`);
  }
}

export function assertIsDocRefIndirectEligibleEntityHandle(
  handle: DocEntityHandle,
): asserts handle is DocRefIndirectEligibleEntityHandle {
  if (!(handle.type in docRefIndirectEligibleEntities)) {
    throw new Error(`Invalid doc ref indirect eligible entity handle: ${handle.type}`);
  }
}

export function isDocRefDirectEligibleEntityType(type: DocEntityType): type is DocRefDirectEligibleEntityType {
  return type in docRefDirectEligibleEntities;
}

export function isDocRefDirectEligibleHandle(handle: DocEntityHandle): handle is DocRefDirectEligibleEntityHandle {
  return handle.type in docRefDirectEligibleEntities;
}

export function isDocRefIndirectEligibleEntityType(type: DocEntityType): type is DocRefIndirectEligibleEntityType {
  return type in docRefIndirectEligibleEntities;
}

export function isDocRefIndirectEligibleHandle(handle: DocEntityHandle): handle is DocRefIndirectEligibleEntityHandle {
  return handle.type in docRefIndirectEligibleEntities;
}

export function isDocRefEligibleEntityType(type: DocEntityType): type is DocRefEligibleEntityType {
  return isDocRefDirectEligibleEntityType(type) || isDocRefIndirectEligibleEntityType(type);
}

export function isDocRefEligibleHandle(handle: DocEntityHandle): handle is DocRefEligibleEntityHandle {
  return isDocRefDirectEligibleEntityType(handle.type) || isDocRefIndirectEligibleEntityType(handle.type);
}

export function assertIsDocRefEligibleEntityType(type: DocEntityType): asserts type is DocRefEligibleEntityType {
  if (!isDocRefEligibleEntityType(type)) {
    throw new Error(`Invalid doc ref eligible entity type: ${type}`);
  }
}

export function assertIsDocRefEligibleHandle(handle: DocEntityHandle): asserts handle is DocRefEligibleEntityHandle {
  if (!isDocRefEligibleEntityType(handle.type)) {
    throw new Error(`Invalid doc ref eligible entity handle: ${handle.type}`);
  }
}

export function isEmbeddedDocRefEligibleEntityType(type: DocEntityType): type is EmbeddedDocRefEligibleEntityType {
  return type in embeddedDocRefEligibleEntities;
}
